/** Tema claro/oscuro, elegido a mano y persistido en localStorage.
 *
 * Por defecto la app sigue `prefers-color-scheme` del sistema — ver el
 * `data-theme` en styles.css. Elegir acá pisa esa preferencia poniendo
 * `data-theme` en el `<html>`; después del primer render siempre queda un
 * valor explícito, nunca "auto": el switch es un control binario predecible.
 */

import { useEffect, useState } from 'react'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'deltabot:theme'

function getSystemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

/** Pura a propósito, sin tocar localStorage: un valor corrupto o de una
 *  versión vieja no tiene que romper nada, sólo perder la memoria guardada. */
export function parseStoredTheme(raw: string | null): Theme | null {
  return raw === 'light' || raw === 'dark' ? raw : null
}

function load(): Theme | null {
  try {
    return parseStoredTheme(localStorage.getItem(STORAGE_KEY))
  } catch {
    // localStorage deshabilitado (modo privado, por ejemplo): se sigue sin
    // memoria, no es motivo para romper el switch.
    return null
  }
}

function save(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Ídem load(): si no se puede guardar, el switch sigue andando sin memoria.
  }
}

export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(() => load() ?? getSystemTheme())

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    save(theme)
  }, [theme])

  return [theme, () => setTheme((current) => (current === 'dark' ? 'light' : 'dark'))]
}
